import TextEditorMinimal from '@/components/commons/minimal-tiptap/minimal-editor';
import { BlogHomeTitleElementDataType } from '@/components/sections/builder/elements/body/blog-home/type';
import { getOverrideAnimationParams } from '@/components/sections/builder/settings/animation-setting';
import InputSelect from '@/components/sections/builder/settings/input-select';
import PaddingSetting from '@/components/sections/builder/settings/padding-setting';
import { ElementProps } from '@/components/sections/builder/types';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { useBuilderStore } from '@/stores/builder';
import { Type } from 'lucide-react';
import { shallow } from 'zustand/shallow';

const defaultData: BlogHomeTitleElementDataType = {
  name: 'Title',
  label: 'Our Blog',
  headingSize: 'medium',
  padding: {
    top: 1,
    bottom: 16,
    left: 1,
    right: 1,
  },
};

export const ElementContent = ({ element }: ElementProps) => {
  const data = (element.data || defaultData) as BlogHomeTitleElementDataType;

  return (
    <>
      <h2
        data-animation='true'
        {...getOverrideAnimationParams(data.animation)}
        className={cn(
          'theme_font_heading text-3xl tracking-tight',
          'p-3',
          'lg:pb-[--p-bottom] lg:pl-[--p-left] lg:pr-[--p-right] lg:pt-[--p-top]',
          data.headingSize === 'large' && 'text-4xl @sm:text-4xl md:text-5xl lg:text-6xl',
          data.headingSize === 'medium' && 'text-3xl @sm:text-3xl md:text-4xl lg:text-5xl',
          data.headingSize === 'small' && 'text-2xl @sm:text-2xl md:text-3xl lg:text-4xl'
        )}
        dangerouslySetInnerHTML={{ __html: data.label }}
        style={
          {
            '--p-top': `${data.padding?.top || 0}px`,
            '--p-bottom': `${data.padding?.bottom || 0}px`,
            '--p-left': `${data.padding?.left || 0}px`,
            '--p-right': `${data.padding?.right || 0}px`,
          } as React.CSSProperties
        }
      />
    </>
  );
};

export const ElementPreviewIcon = Type;

export function ElementSetting({ element }: ElementProps) {
  const builder = useBuilderStore(
    (state) => ({
      setChildData: state.setChildData,
      clearSelected: state.clearSelected,
    }),
    shallow
  );

  const data = element.data as BlogHomeTitleElementDataType;

  return (
    <div>
      <h1 className='mb-3 text-sm font-bold'>Section Header</h1>
      <div className='mb-3 flex flex-col gap-2'>
        <h4>Text</h4>
        <TextEditorMinimal
          onChange={(value) =>
            builder.setChildData(element.parentId as string, element.id, {
              label: value,
            })
          }
          value={data.label}
          placeholder='Label'
          className='mb-2 w-full '
          output='html'
          editorProps={{
            attributes: {
              class: 'prose lg:prose-lg prose-blue dark:prose-invert p-2',
            },
          }}
          editable
        />
      </div>
      <div className='mb-3 grid grid-cols-2 items-center gap-2'>
        <h4>Heading Size</h4>
        <InputSelect
          defaultValue={data.headingSize || defaultData.headingSize}
          options={[
            { label: 'Small', value: 'small' },
            { label: 'Medium', value: 'medium' },
            { label: 'Large', value: 'large' },
          ]}
          onChange={(v) =>
            builder.setChildData(element.parentId as string, element.id, { headingSize: v })
          }
        />
      </div>

      <Separator className='my-4' />

      <h1 className='mb-3 text-sm font-bold'>Style</h1>
      <PaddingSetting
        value={data.padding}
        hideLeft={true}
        hideRight={true}
        onChange={(v) =>
          builder.setChildData(element.parentId as string, element.id, {
            padding: v,
          })
        }
      />
    </div>
  );
}

const BlogHomeTitle = {
  type: 'blog_home_title',
  group: 'blog_home',
  defaultData,
  Element: ElementContent,
  Setting: ElementSetting,
  PreviewIcon: ElementPreviewIcon,
  Icon: ElementPreviewIcon,
  childs: [],
};

export default BlogHomeTitle;
